import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormLabel,
    Grid,
    ListSubheader,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { SdohAssessmentEnum } from '../../../Enum/SdohAssessmentEnum';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { FamilyAndHome } from '../../../Models/Sdoh/FamilyAndHome.model';
import { MoneyAndResources } from '../../../Models/Sdoh/MoneyAndResources.model';
import { OptionalAdditionalQuestions } from '../../../Models/Sdoh/OptionalAdditionalQuestions.model';
import { PersonalCharacteristics } from '../../../Models/Sdoh/PersonalCharacteristics.model';
import { SdohAssessment } from '../../../Models/Sdoh/SdohAssessment.model';
import { SdohSingleAnswerAdvanced } from '../../../Models/Sdoh/SdohSingleAnswerAdvanced.model';
import { SocialAndEmotionalHealth } from '../../../Models/Sdoh/SocialAndEmotionalHealth.model';
import { RootState } from '../../../reducers';
import {
    fetchSdohAssessment,
    getLastSdohAssessment,
    gotoNextMenu,
    gotoPreviousMenu,
    saveSdohAssessment,
    updateSdohAssessment,
    prepareSdohRequest,
    saveSdohDemographicsInfo,
} from '../../../store/assessment.slice';
import { assessmentStatusEnum } from '../../../utils/assessments';
import { copyObject, highlightStyle } from '../../../utils/common';
import {
    afraidOfPartnerList,
    communicationBarriersList,
    emotionallySafeList,
    frequencyOfTalkingPeopleList,
    housingSituationList,
    languageList,
    levelOfSchoolList,
    livingSituationOtherList,
    livingSituationResidentialAndTreatmentList,
    livingSituationTypesList,
    modeOfTransportationList,
    sexualOrientationList,
    stressLevelList,
    totalIncomeList,
    workingSituationList,
    yesNoList,
} from '../../../utils/constants';
import SdohNeedsIdentifiedModal from './SdohNeedsIdentifiedModal';
import { fetchPreferredPronouns, fetchRaces } from '../../../store/memberDemographicsDetails.slice';
import { getMemberBasicInfo, setMemberBasicInfo } from '../../../store/memberDetail.slice';
import { PreferredPronounsEnum } from '../../../Enum/PreferredPronounsEnum';
import { RaceEnum } from '../../../Enum/RaceEnum';
import { DiagnosticType } from '../../../Enum/DiagnosticType';
import BHDiagnosticConfirmationBody from '../../Shared/BHDiagnosticConfirmationBody';
import { SdohMultiAnswer } from '../../../Models/Sdoh/SdohMultiAnswer.model';
import { SdohSingleAnswer } from '../../../Models/Sdoh/SdohSingleAnswer.model';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_SDOH_Hamilton_Anxiety_Scale.pdf';

const theme = createTheme();

const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '75%',
        },
    },
};

const Sdoh: React.FC<{ id?: string; memberId: string }> = ({ id, memberId }) => {
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [showNeedsIdentifiedModal, setShowNeedsIdentifiedModal] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { sdohAssessment, fetchingSdoh, isSaving } = useSelector((state: RootState) => state.assessment);
    const { engagementDraft } = useSelector((state: RootState) => state.documentEngagement);
    const isBHDiagnostic = engagementDraft.DiagnosticStatus?.DiagnosticType === DiagnosticType.BHDiagnostic;
    const { preferredPronouns, races } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const { memberBasicInfo } = useSelector((state: RootState) => state.memberDetail);
    const [demographicsIsChanged, setDemographicsIsChanged] = useState<boolean>(false);
    const [initialInfo, setInitialInfo] = useState<SdohAssessment>({
        Id: '',
        MemberId: '',
        MemberName: '',
        PersonalCharacteristics: new PersonalCharacteristics(),
        FamilyAndHome: new FamilyAndHome(),
        MoneyAndResources: new MoneyAndResources(),
        SocialAndEmotionalHealth: new SocialAndEmotionalHealth(),
        OptionalAdditionalQuestions: new OptionalAdditionalQuestions(),
        Status: StatusEnum.None,
        RequestIds: [],
        ResourceOptions: [],
        ResourceRequestIds: [],
        Comment: '',
        IsLast: false,
    });

    const sdohIsComplete = (assessment: SdohAssessment): boolean => {
        const requiredItems = [
            assessment.PersonalCharacteristics?.LanguageAreMostComfortableSpeaking?.Answer,
            assessment.PersonalCharacteristics?.CommunicationBarriers?.Answer,
            assessment.FamilyAndHome?.HousingSituationToday?.Answer,
            assessment.MoneyAndResources?.HighestLevelOfSchoolYouFinished?.Answer,
            assessment.MoneyAndResources?.CurrentWorkSituation?.Answer,
            assessment.MoneyAndResources?.TotalIncome?.Answer,
            assessment.MoneyAndResources?.NeededThingsUnableToGetInPastYear?.Answer?.length > 0,
            assessment.SocialAndEmotionalHealth?.HowOftenDoYouSeeOrTalkToPeople?.Answer,
            assessment.SocialAndEmotionalHealth?.HowStressedAreYou?.Answer,
        ];

        return requiredItems.every((item) => item !== undefined && item !== null && item !== '');
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const saveSdoh = (sdohInfo: SdohAssessment, isWithoutComplete = false) => {
        if (demographicsIsChanged) {
            dispatch(saveSdohDemographicsInfo(memberBasicInfo));
        }
        let data = prepareSdohInfo(copyObject(sdohInfo));
        data.MemberId = memberId;
        data.Status = sdohInfo.Status;
        if (isWithoutComplete) {
            data.Status = StatusEnum.Completed;
        }
        if (sdohInfo.Id) {
            delete data.Comment;
            data.Id = sdohInfo.Id;
            dispatch(updateSdohAssessment(data));
        } else {
            if (!isWithoutComplete) {
                data.Status = assessmentStatusEnum.IN_PROGRESS;
            }
            dispatch(saveSdohAssessment(data));
        }
        setShowNeedsIdentifiedModal(true);
    };

    const prepareSdohInfo = (sdohInfo: SdohAssessment) => {
        sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer = [];

        if (sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear === null)
            sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear = new SdohMultiAnswer();
        sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer = [];

        if (sdohInfo.MoneyAndResources.Food && sdohInfo.MoneyAndResources.Food.Answer) {
            let foodDetail = new SdohSingleAnswer();
            foodDetail.Answer = SdohAssessmentEnum.Food;
            if (sdohInfo.MoneyAndResources.Food && sdohInfo.MoneyAndResources.Food.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(foodDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(foodDetail);
        }

        if (sdohInfo.MoneyAndResources.SnapBenefits && sdohInfo.MoneyAndResources.SnapBenefits.Answer) {
            let snapBenefitsDetail = new SdohSingleAnswer();
            snapBenefitsDetail.Answer = SdohAssessmentEnum.SnapBenefits;
            if (sdohInfo.MoneyAndResources.SnapBenefits && sdohInfo.MoneyAndResources.SnapBenefits.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(snapBenefitsDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(snapBenefitsDetail);
        }

        if (sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits && sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits.Answer) {
            let helpApplyingForSnapBenefitsDetail = new SdohSingleAnswer();
            helpApplyingForSnapBenefitsDetail.Answer = SdohAssessmentEnum.HelpApplyingForSnapBenefits;
            if (
                sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits &&
                sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits.Answer === SdohAssessmentEnum.Yes
            )
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(helpApplyingForSnapBenefitsDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(helpApplyingForSnapBenefitsDetail);
        }

        if (sdohInfo.MoneyAndResources.Clothing && sdohInfo.MoneyAndResources.Clothing.Answer) {
            let clothingDetail = new SdohSingleAnswer();
            clothingDetail.Answer = SdohAssessmentEnum.Clothing;
            if (sdohInfo.MoneyAndResources.Clothing && sdohInfo.MoneyAndResources.Clothing.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(clothingDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(clothingDetail);
        }

        if (sdohInfo.MoneyAndResources.Utilities && sdohInfo.MoneyAndResources.Utilities.Answer) {
            let utilitiesDetail = new SdohSingleAnswer();
            utilitiesDetail.Answer = SdohAssessmentEnum.Utilities;
            if (sdohInfo.MoneyAndResources.Utilities && sdohInfo.MoneyAndResources.Utilities.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(utilitiesDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(utilitiesDetail);
        }
        if (sdohInfo.MoneyAndResources.ChildCare && sdohInfo.MoneyAndResources.ChildCare.Answer) {
            let childCareDetail = new SdohSingleAnswer();
            childCareDetail.Answer = SdohAssessmentEnum.ChildCare;
            if (sdohInfo.MoneyAndResources.ChildCare && sdohInfo.MoneyAndResources.ChildCare.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(childCareDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(childCareDetail);
        }
        if (sdohInfo.MoneyAndResources.Phone && sdohInfo.MoneyAndResources.Phone.Answer) {
            let phoneDetail = new SdohSingleAnswer();
            phoneDetail.Answer = SdohAssessmentEnum.Phone;
            if (sdohInfo.MoneyAndResources.Phone && sdohInfo.MoneyAndResources.Phone.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(phoneDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(phoneDetail);
        }
        if (sdohInfo.MoneyAndResources.Medicine && sdohInfo.MoneyAndResources.Medicine.Answer) {
            let healthCareDetail = new SdohSingleAnswer();
            healthCareDetail.Answer = SdohAssessmentEnum.Medicine;
            if (sdohInfo.MoneyAndResources.Medicine && sdohInfo.MoneyAndResources.Medicine.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(healthCareDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(healthCareDetail);
        }
        if (sdohInfo.MoneyAndResources.OtherNeed && sdohInfo.MoneyAndResources.OtherNeed.Answer) {
            let otherNeedDetail = new SdohSingleAnswer();
            otherNeedDetail.Answer = SdohAssessmentEnum.OtherNeed;
            if (sdohInfo.MoneyAndResources.OtherNeed && sdohInfo.MoneyAndResources.OtherNeed.Answer === SdohAssessmentEnum.Yes)
                sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.push(otherNeedDetail);
            else sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.push(otherNeedDetail);
        }
        return sdohInfo;
    };

    const prepareOtherNeeds = useCallback(() => {
        let sdohInfo = copyObject(sdohAssessment);
        sdohInfo.MoneyAndResources.Food = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.SnapBenefits = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.Clothing = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.Utilities = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.ChildCare = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.Phone = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.Medicine = new SdohSingleAnswerAdvanced();
        sdohInfo.MoneyAndResources.OtherNeed = new SdohSingleAnswerAdvanced();
        if (Boolean(sdohInfo.MoneyAndResources?.NeededThingsUnableToGetInPastYear?.Answer?.length)) {
            sdohInfo.MoneyAndResources.NeededThingsUnableToGetInPastYear.Answer.forEach((thing) => {
                if (thing.Answer === SdohAssessmentEnum.Food) sdohInfo.MoneyAndResources.Food.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.SnapBenefits) sdohInfo.MoneyAndResources.SnapBenefits.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.HelpApplyingForSnapBenefits)
                    sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.Clothing) sdohInfo.MoneyAndResources.Clothing.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.Utilities) sdohInfo.MoneyAndResources.Utilities.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.ChildCare) sdohInfo.MoneyAndResources.ChildCare.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.Phone) sdohInfo.MoneyAndResources.Phone.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.Medicine) sdohInfo.MoneyAndResources.Medicine.Answer = SdohAssessmentEnum.Yes;
                else if (thing.Answer === SdohAssessmentEnum.OtherNeed) sdohInfo.MoneyAndResources.OtherNeed.Answer = SdohAssessmentEnum.Yes;
            });
        }
        if (Boolean(sdohInfo.MoneyAndResources?.NotNeededThingsUnableToGetInPastYear?.Answer?.length)) {
            sdohInfo.MoneyAndResources.NotNeededThingsUnableToGetInPastYear.Answer.forEach((thing) => {
                if (thing.Answer === SdohAssessmentEnum.Food) sdohInfo.MoneyAndResources.Food.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.SnapBenefits) sdohInfo.MoneyAndResources.SnapBenefits.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.HelpApplyingForSnapBenefits)
                    sdohInfo.MoneyAndResources.HelpApplyingForSnapBenefits.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.Clothing) sdohInfo.MoneyAndResources.Clothing.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.Utilities) sdohInfo.MoneyAndResources.Utilities.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.ChildCare) sdohInfo.MoneyAndResources.ChildCare.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.Phone) sdohInfo.MoneyAndResources.Phone.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.Medicine) sdohInfo.MoneyAndResources.Medicine.Answer = SdohAssessmentEnum.No;
                else if (thing.Answer === SdohAssessmentEnum.OtherNeed) sdohInfo.MoneyAndResources.OtherNeed.Answer = SdohAssessmentEnum.No;
            });
        }
        return sdohInfo;
    }, [sdohAssessment]);

    const initialPage = useCallback(() => {
        if (Boolean(engagementDraft?.SdohAssessmentId)) {
            dispatch(fetchSdohAssessment(engagementDraft.SdohAssessmentId));
        } else {
            dispatch(getLastSdohAssessment(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, id]);

    useEffect(() => {
        if (sdohAssessment && (sdohAssessment.Id || sdohAssessment.IsLast)) {
            setInitialInfo(prepareOtherNeeds());
        }
    }, [sdohAssessment, prepareOtherNeeds]);

    useEffect(() => {
        dispatch(fetchPreferredPronouns());
        dispatch(fetchRaces());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getMemberBasicInfo(memberId));
    }, [dispatch, memberId]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    return (
        <Formik initialValues={initialInfo} onSubmit={handleSubmit} enableReinitialize={true}>
            {({ values, setFieldValue, errors, handleChange }: FormikProps<SdohAssessment>) => (
                <Form>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 2 }}>
                        <Typography variant="h6">SDOH Assessment</Typography>
                        <Tooltip title="SDOH Assessment Help Document">
                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                        </Tooltip>
                    </Stack>
                    <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                        <Grid className="content-form">
                            {fetchingSdoh ? (
                                <Box display="flex" justifyContent="center" className="loader-container">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Grid container spacing={2} sx={classes.root}>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Typography variant="h6" component="label">
                                                Personal Characteristics
                                            </Typography>
                                            {isBHDiagnostic && (
                                                <Typography variant="body2" component="label" ml={5} sx={highlightStyle}>
                                                    * Required for BH Diagnostic
                                                </Typography>
                                            )}
                                            <hr />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_WhatLanguageAreYouMostComfortableSpeaking}
                                                {values.PersonalCharacteristics?.IsAILanguageAreMostComfortableSpeaking && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="PersonalCharacteristics.LanguageAreMostComfortableSpeaking.Answer"
                                                value={values.PersonalCharacteristics?.LanguageAreMostComfortableSpeaking?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.LanguageAreMostComfortableSpeaking.Answer', e.currentTarget.value);
                                                    setFieldValue('PersonalCharacteristics.LanguageAreMostComfortableSpeaking.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAILanguageAreMostComfortableSpeaking', false);
                                                }}
                                            >
                                                {languageList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.LanguageAreMostComfortableSpeaking?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.LanguageAreMostComfortableSpeaking.Answer', null);
                                                    setFieldValue('PersonalCharacteristics.LanguageAreMostComfortableSpeaking.OtherAnswer', null);
                                                    setFieldValue(
                                                        'PersonalCharacteristics.LanguageAreMostComfortableSpeaking.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('PersonalCharacteristics.IsAILanguageAreMostComfortableSpeaking', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {values.PersonalCharacteristics?.LanguageAreMostComfortableSpeaking?.Answer ===
                                            SdohAssessmentEnum.LanguageOtherThan ? (
                                                <TextField
                                                    label=""
                                                    variant="outlined"
                                                    size="small"
                                                    value={values.PersonalCharacteristics.LanguageAreMostComfortableSpeaking.OtherAnswer}
                                                    name="PersonalCharacteristics.LanguageAreMostComfortableSpeaking.OtherAnswer"
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setFieldValue('PersonalCharacteristics.IsAILanguageAreMostComfortableSpeaking', false);
                                                    }}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_DoYouHaveProblemsReading}
                                            {values.PersonalCharacteristics?.IsAIDoYouHaveProblemsReading && isAIIcon()}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="PersonalCharacteristics.DoYouHaveProblemsReading.Answer"
                                                value={values.PersonalCharacteristics?.DoYouHaveProblemsReading?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsReading.Answer', e.currentTarget.value);
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsReading.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAIDoYouHaveProblemsReading', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer-lable2-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.DoYouHaveProblemsReading?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsReading.Answer', null);
                                                    setFieldValue(
                                                        'PersonalCharacteristics.DoYouHaveProblemsReading.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('PersonalCharacteristics.IsAIDoYouHaveProblemsReading', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_DoYouHaveProblemsWriting}
                                            {values.PersonalCharacteristics?.IsAIDoYouHaveProblemsWriting && isAIIcon()}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="PersonalCharacteristics.DoYouHaveProblemsWriting.Answer"
                                                value={values.PersonalCharacteristics?.DoYouHaveProblemsWriting?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsWriting.Answer', e.currentTarget.value);
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsWriting.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAIDoYouHaveProblemsWriting', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer-lable2-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.DoYouHaveProblemsWriting?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.DoYouHaveProblemsWriting.Answer', null);
                                                    setFieldValue(
                                                        'PersonalCharacteristics.DoYouHaveProblemsWriting.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('PersonalCharacteristics.IsAIDoYouHaveProblemsWriting', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_DoYouHaveProblemsReadingOrUnderstandingHealthLiterature}
                                            {values.PersonalCharacteristics?.IsAIHaveProblemsInReadingHealthLiterature && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.Answer"
                                                value={values.PersonalCharacteristics?.HaveProblemsInReadingHealthLiterature?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue(
                                                        'PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.Answer',
                                                        e.currentTarget.value
                                                    );
                                                    setFieldValue('PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAIHaveProblemsInReadingHealthLiterature', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer-lable2-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.HaveProblemsInReadingHealthLiterature?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.Answer', null);
                                                    setFieldValue(
                                                        'PersonalCharacteristics.HaveProblemsInReadingHealthLiterature.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('PersonalCharacteristics.IsAIHaveProblemsInReadingHealthLiterature', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_WhatIsYourSexualOrientation}
                                            {values.PersonalCharacteristics?.IsAISexualOrientation && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="PersonalCharacteristics.SexualOrientation.Answer"
                                                value={values.PersonalCharacteristics?.SexualOrientation?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.SexualOrientation.Answer', e.currentTarget.value);
                                                    setFieldValue('PersonalCharacteristics.SexualOrientation.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAISexualOrientation', false);
                                                }}
                                            >
                                                {sexualOrientationList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer13-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.SexualOrientation?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.SexualOrientation.Answer', null);
                                                    setFieldValue('PersonalCharacteristics.SexualOrientation.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('PersonalCharacteristics.IsAISexualOrientation', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {values.PersonalCharacteristics?.SexualOrientation?.Answer === SdohAssessmentEnum.SelfIdentified ? (
                                                <TextField
                                                    label=""
                                                    variant="outlined"
                                                    size="small"
                                                    value={values.PersonalCharacteristics.SexualOrientation.OtherAnswer}
                                                    name="PersonalCharacteristics.SexualOrientation.OtherAnswer"
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setFieldValue('PersonalCharacteristics.IsAISexualOrientation', false);
                                                    }}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>What is your preferred pronouns?</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Select
                                                value={memberBasicInfo?.PreferredPronouns || 0}
                                                onChange={(e) => {
                                                    dispatch(setMemberBasicInfo({ ...memberBasicInfo, PreferredPronouns: Number(e.target.value) }));
                                                    setDemographicsIsChanged(true);
                                                    setFieldValue('PersonalCharacteristics.PreferredPronouns.NoAnswerToQuestion', false);
                                                }}
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                sx={{ width: '300px' }}
                                            >
                                                {preferredPronouns.map((type, index) => {
                                                    return (
                                                        <MenuItem key={`answer13-label-${index}`} value={type.value as PreferredPronounsEnum}>
                                                            {type.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.PreferredPronouns?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    dispatch(setMemberBasicInfo({ ...memberBasicInfo, PreferredPronouns: null }));
                                                    setDemographicsIsChanged(false);
                                                    setFieldValue('PersonalCharacteristics.PreferredPronouns.NoAnswerToQuestion', e.currentTarget.checked);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {memberBasicInfo?.PreferredPronouns === PreferredPronounsEnum.Other ? (
                                                <TextField
                                                    label=""
                                                    variant="outlined"
                                                    size="small"
                                                    placeholder="Please specify"
                                                    value={memberBasicInfo.PreferredPronounsOther || ''}
                                                    name="PersonalCharacteristics.PreferredPronounsOther.OtherAnswer"
                                                    onChange={(e) => {
                                                        dispatch(setMemberBasicInfo({ ...memberBasicInfo, PreferredPronounsOther: e.target.value }));
                                                        setDemographicsIsChanged(true);
                                                    }}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>What is your race?</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Select
                                                value={memberBasicInfo?.Race || ''}
                                                onChange={(e) => {
                                                    dispatch(setMemberBasicInfo({ ...memberBasicInfo, Race: e.target.value as RaceEnum }));
                                                    setDemographicsIsChanged(true);
                                                    setFieldValue('PersonalCharacteristics.Race.NoAnswerToQuestion', false);
                                                }}
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                sx={{ width: '300px' }}
                                            >
                                                {races.map((type, index) => {
                                                    return (
                                                        <MenuItem key={`answer13-label-${index}`} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.Race?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    dispatch(setMemberBasicInfo({ ...memberBasicInfo, Race: null }));
                                                    setDemographicsIsChanged(false);
                                                    setFieldValue('PersonalCharacteristics.Race.NoAnswerToQuestion', e.currentTarget.checked);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_AreThereAnyCommunicationBarriersThatImpactTheAssessment}
                                                {values.PersonalCharacteristics?.IsAICommunicationBarriers && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Select
                                                value={values.PersonalCharacteristics?.CommunicationBarriers?.Answer || ''}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.CommunicationBarriers.Answer', e.target.value);
                                                    setFieldValue('PersonalCharacteristics.CommunicationBarriers.NoAnswerToQuestion', false);
                                                    setFieldValue('PersonalCharacteristics.IsAICommunicationBarriers', false);
                                                }}
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                sx={{ width: '300px' }}
                                            >
                                                {communicationBarriersList.map((type, index) => {
                                                    return (
                                                        <MenuItem key={`answer13-label-${index}`} value={type.Answer}>
                                                            {type.Answer}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.PersonalCharacteristics?.CommunicationBarriers?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('PersonalCharacteristics.CommunicationBarriers.Answer', null);
                                                    setFieldValue('PersonalCharacteristics.CommunicationBarriers.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('PersonalCharacteristics.IsAICommunicationBarriers', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Typography variant="h6" component="label">
                                                Family and Home
                                            </Typography>
                                            <hr />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_HowManyFamilyMembersIncludingYourselfDoYouCurrentlyLiveWith}
                                            {values.FamilyAndHome?.IsAIFamilyMembersCurrentlyLiveWithYou && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <TextField
                                                label=""
                                                variant="outlined"
                                                size="small"
                                                value={values.FamilyAndHome?.FamilyMembersCurrentlyLiveWithYou?.Answer}
                                                name="FamilyAndHome.FamilyMembersCurrentlyLiveWithYou.Answer"
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.FamilyMembersCurrentlyLiveWithYou.Answer', e.currentTarget.value);
                                                    setFieldValue('FamilyAndHome.FamilyMembersCurrentlyLiveWithYou.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAIFamilyMembersCurrentlyLiveWithYou', false);
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.FamilyMembersCurrentlyLiveWithYou?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.FamilyMembersCurrentlyLiveWithYou.Answer', '');
                                                    setFieldValue(
                                                        'FamilyAndHome.FamilyMembersCurrentlyLiveWithYou.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('FamilyAndHome.IsAIFamilyMembersCurrentlyLiveWithYou', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_WhatIsYourHousingSituationToday}
                                                {values.FamilyAndHome?.IsAIHousingSituationToday && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="FamilyAndHome.HousingSituationToday.Answer"
                                                value={values.FamilyAndHome?.HousingSituationToday?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.HousingSituationToday.Answer', e.currentTarget.value);
                                                    setFieldValue('FamilyAndHome.HousingSituationToday.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAIHousingSituationToday', false);
                                                }}
                                            >
                                                {housingSituationList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer2-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.HousingSituationToday?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.HousingSituationToday.Answer', null);
                                                    setFieldValue('FamilyAndHome.HousingSituationToday.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('FamilyAndHome.IsAIHousingSituationToday', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_AreYouWorriedAboutLosingYourHousing}
                                            {values.FamilyAndHome?.IsAIWorriedAboutLosingYourHousing && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="FamilyAndHome.WorriedAboutLosingYourHousing.Answer"
                                                value={values.FamilyAndHome?.WorriedAboutLosingYourHousing?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.WorriedAboutLosingYourHousing.Answer', e.currentTarget.value);
                                                    setFieldValue('FamilyAndHome.WorriedAboutLosingYourHousing.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAIWorriedAboutLosingYourHousing', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer3-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.WorriedAboutLosingYourHousing?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.WorriedAboutLosingYourHousing.Answer', null);
                                                    setFieldValue('FamilyAndHome.WorriedAboutLosingYourHousing.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('FamilyAndHome.IsAIWorriedAboutLosingYourHousing', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_WhatIsYourCurrentLivingSituation}
                                            {values.FamilyAndHome?.IsAICurrentLivingSituation && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Select
                                                value={values.FamilyAndHome?.CurrentLivingSituation?.Answer || ''}
                                                fullWidth
                                                size="small"
                                                sx={{ width: '300px' }}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.CurrentLivingSituation.Answer', e.target.value);
                                                    setFieldValue('FamilyAndHome.CurrentLivingSituation.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAICurrentLivingSituation', false);
                                                }}
                                            >
                                                <MenuItem value="Member's home" sx={{ pl: 4 }}>
                                                    Member's home
                                                </MenuItem>
                                                <ListSubheader>Residential Care/Treatment Facility</ListSubheader>
                                                {livingSituationResidentialAndTreatmentList.map((type, index) => (
                                                    <MenuItem key={`answer13-label-${index}`} value={type.Answer} sx={{ pl: 4 }}>
                                                        {type.Answer}
                                                    </MenuItem>
                                                ))}
                                                <ListSubheader>Other</ListSubheader>
                                                {livingSituationOtherList.map((type, index) => (
                                                    <MenuItem key={`answer13-label-${index}`} value={type.Answer} sx={{ pl: 4 }}>
                                                        {type.Answer}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.CurrentLivingSituation?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.CurrentLivingSituation.Answer', null);
                                                    setFieldValue('FamilyAndHome.CurrentLivingSituation.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('FamilyAndHome.IsAICurrentLivingSituation', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_WhatTypeOfLivingSituation}
                                            {values.FamilyAndHome?.IsAILivingSituationType && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="FamilyAndHome.LivingSituationType.Answer"
                                                value={values.FamilyAndHome?.LivingSituationType?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.LivingSituationType.Answer', e.currentTarget.value);
                                                    setFieldValue('FamilyAndHome.LivingSituationType.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAILivingSituationType', false);
                                                }}
                                            >
                                                {livingSituationTypesList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer13-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.LivingSituationType?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.LivingSituationType.Answer', null);
                                                    setFieldValue('FamilyAndHome.LivingSituationType.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('FamilyAndHome.IsAILivingSituationType', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Describe your current living conditions:</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <TextField
                                                label=""
                                                variant="outlined"
                                                multiline
                                                placeholder="Running water, electricity, safety, etc."
                                                rows={4}
                                                fullWidth
                                                size="small"
                                                value={values.FamilyAndHome?.CurrentLivingConditions?.Answer}
                                                name="FamilyAndHome.CurrentLivingConditions.Answer"
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.CurrentLivingConditions.Answer', e.currentTarget.value);
                                                    setFieldValue('FamilyAndHome.CurrentLivingConditions.NoAnswerToQuestion', false);
                                                    setFieldValue('FamilyAndHome.IsAICurrentLivingConditions', false);
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.FamilyAndHome?.CurrentLivingConditions?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('FamilyAndHome.CurrentLivingConditions.Answer', null);
                                                    setFieldValue('FamilyAndHome.CurrentLivingConditions.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('FamilyAndHome.IsAICurrentLivingConditions', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Typography variant="h6" component="label">
                                                Money and Resources
                                            </Typography>
                                            <hr />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_WhatIsTheHighestLevelOfSchoolThatYouHaveFinished}
                                                {values.MoneyAndResources?.IsAIHighestLevelOfSchoolYouFinished && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.HighestLevelOfSchoolYouFinished.Answer"
                                                value={values.MoneyAndResources?.HighestLevelOfSchoolYouFinished?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.HighestLevelOfSchoolYouFinished.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.HighestLevelOfSchoolYouFinished.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAIHighestLevelOfSchoolYouFinished', false);
                                                }}
                                            >
                                                {levelOfSchoolList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer4-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.MoneyAndResources?.HighestLevelOfSchoolYouFinished?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.HighestLevelOfSchoolYouFinished.Answer', null);
                                                    setFieldValue(
                                                        'MoneyAndResources.HighestLevelOfSchoolYouFinished.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('MoneyAndResources.IsAIHighestLevelOfSchoolYouFinished', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_WhatIsYourCurrentModeOfTransportation}
                                            {values.MoneyAndResources?.IsAICurrentModeOfTransportation && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.CurrentModeOfTransportation.Answer"
                                                value={values.MoneyAndResources?.CurrentModeOfTransportation?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.CurrentModeOfTransportation.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.CurrentModeOfTransportation.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAICurrentModeOfTransportation', false);
                                                }}
                                            >
                                                {modeOfTransportationList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer-lable2-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.MoneyAndResources?.CurrentModeOfTransportation?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.CurrentModeOfTransportation.Answer', null);
                                                    setFieldValue('MoneyAndResources.CurrentModeOfTransportation.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('MoneyAndResources.IsAICurrentModeOfTransportation', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_WhatIsYourCurrentWorkSituation}
                                                {values.MoneyAndResources?.IsAICurrentWorkSituation && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.CurrentWorkSituation.Answer"
                                                value={values.MoneyAndResources?.CurrentWorkSituation?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.CurrentWorkSituation.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.CurrentWorkSituation.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAICurrentWorkSituation', false);
                                                }}
                                            >
                                                {workingSituationList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer6-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.MoneyAndResources?.CurrentWorkSituation?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.CurrentWorkSituation.Answer', null);
                                                    setFieldValue('MoneyAndResources.CurrentWorkSituation.OtherAnswer', null);
                                                    setFieldValue('MoneyAndResources.CurrentWorkSituation.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('MoneyAndResources.IsAICurrentWorkSituation', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {values.MoneyAndResources?.CurrentWorkSituation?.Answer === SdohAssessmentEnum.MoneyAndResources ? (
                                                <TextField
                                                    label=""
                                                    variant="outlined"
                                                    size="small"
                                                    placeholder="Please specify"
                                                    value={values.MoneyAndResources?.CurrentWorkSituation?.OtherAnswer}
                                                    name="MoneyAndResources.CurrentWorkSituation.OtherAnswer"
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setFieldValue('MoneyAndResources.IsAICurrentWorkSituation', false);
                                                    }}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_DuringThePastYearWhatWasTheTotalCombinedIncome}
                                                {values.MoneyAndResources?.IsAITotalIncome && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.TotalIncome.Answer"
                                                value={values.MoneyAndResources?.TotalIncome?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.TotalIncome.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.TotalIncome.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAITotalIncome', false);
                                                }}
                                            >
                                                {totalIncomeList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer7-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.MoneyAndResources?.TotalIncome?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.TotalIncome.Answer', null);
                                                    setFieldValue('MoneyAndResources.TotalIncome.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('MoneyAndResources.IsAITotalIncome', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_HaveYouOrAnyFamilyMembersBeenUnableToGetAnyOfTheFollowing}
                                                {values.MoneyAndResources?.IsAINeededThingsUnableToGetInPastYear && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Food</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.Food.Answer"
                                                value={values.MoneyAndResources?.Food?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Food.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer8-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Do you currently have SNAP benefits?</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.SnapBenefits.Answer"
                                                value={values.MoneyAndResources?.SnapBenefits?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.SnapBenefits.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer8-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        {values.MoneyAndResources?.SnapBenefits?.Answer === 'No' && (
                                            <>
                                                <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                                    <FormLabel>Do you need assistance applying for SNAP benefits?</FormLabel>
                                                </Grid>
                                                <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                                    <RadioGroup
                                                        row
                                                        name="MoneyAndResources.HelpApplyingForSnapBenefits.Answer"
                                                        value={values.MoneyAndResources?.HelpApplyingForSnapBenefits?.Answer}
                                                        onChange={(e) => {
                                                            setFieldValue('MoneyAndResources.HelpApplyingForSnapBenefits.Answer', e.currentTarget.value);
                                                            setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                            setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                            setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                        }}
                                                    >
                                                        {yesNoList.map((type, index) => {
                                                            return (
                                                                <FormControlLabel
                                                                    key={`answer8-label-${index}`}
                                                                    value={type.Answer}
                                                                    control={<Radio />}
                                                                    label={type.Answer}
                                                                />
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </Grid>
                                            </>
                                        )}
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Clothing</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.Clothing.Answer"
                                                value={values.MoneyAndResources?.Clothing?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Clothing.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                            key={`clothing-${index}`}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Utilities</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.Utilities.Answer"
                                                value={values.MoneyAndResources?.Utilities?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Utilities.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer9-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Child Care</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.ChildCare.Answer"
                                                value={values.MoneyAndResources?.ChildCare?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.ChildCare.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer9-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Phone</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.Phone.Answer"
                                                value={values.MoneyAndResources?.Phone?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Phone.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer10-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Medicine or any Health Care (Medical, Dental, Mental Health, Vision)</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.Medicine.Answer"
                                                value={values.MoneyAndResources?.Medicine?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Medicine.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer11-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>Other (Please write)</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="MoneyAndResources.OtherNeed.Answer"
                                                value={values.MoneyAndResources?.OtherNeed?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.OtherNeed.Answer', e.currentTarget.value);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion', false);
                                                    setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                    setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                }}
                                            >
                                                {yesNoList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer12-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.MoneyAndResources?.NeededThingsUnableToGetInPastYear?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('MoneyAndResources.Food.Answer', null);
                                                    setFieldValue('MoneyAndResources.Clothing.Answer', null);
                                                    setFieldValue('MoneyAndResources.Utilities.Answer', null);
                                                    setFieldValue('MoneyAndResources.ChildCare.Answer', null);
                                                    setFieldValue('MoneyAndResources.Phone.Answer', null);
                                                    setFieldValue('MoneyAndResources.Medicine.Answer', null);
                                                    setFieldValue('MoneyAndResources.OtherNeed.Answer', null);
                                                    setFieldValue('MoneyAndResources.NeededThingsUnableToGetInPastYear.OtherAnswer', null);
                                                    setFieldValue(
                                                        'MoneyAndResources.NeededThingsUnableToGetInPastYear.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {values.MoneyAndResources.OtherNeed.Answer === SdohAssessmentEnum.Yes ? (
                                                <TextField
                                                    label=""
                                                    variant="outlined"
                                                    size="small"
                                                    value={values.MoneyAndResources?.NeededThingsUnableToGetInPastYear?.OtherAnswer}
                                                    name="MoneyAndResources.NeededThingsUnableToGetInPastYear.OtherAnswer"
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setFieldValue('MoneyAndResources.IsAINeededThingsUnableToGetInPastYear', false);
                                                        setFieldValue('MoneyAndResources.IsAINotNeededThingsUnableToGetInPastYear', false);
                                                    }}
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Typography variant="h6" component="label">
                                                Social and Emotional Health
                                            </Typography>
                                            <hr />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_HowOftenDoYouSeeOrTalkToPeopleThatYouCareAbout}
                                                {values.SocialAndEmotionalHealth?.IsAIHowOftenDoYouSeeOrTalkToPeople && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="SocialAndEmotionalHealth.HowOftenDoYouSeeOrTalkToPeople.Answer"
                                                value={values.SocialAndEmotionalHealth?.HowOftenDoYouSeeOrTalkToPeople?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('SocialAndEmotionalHealth.HowOftenDoYouSeeOrTalkToPeople.Answer', e.currentTarget.value);
                                                    setFieldValue('SocialAndEmotionalHealth.HowOftenDoYouSeeOrTalkToPeople.NoAnswerToQuestion', false);
                                                    setFieldValue('SocialAndEmotionalHealth.IsAIHowOftenDoYouSeeOrTalkToPeople', false);
                                                }}
                                            >
                                                {frequencyOfTalkingPeopleList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer12-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.SocialAndEmotionalHealth?.HowOftenDoYouSeeOrTalkToPeople?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('SocialAndEmotionalHealth.HowOftenDoYouSeeOrTalkToPeople.Answer', null);
                                                    setFieldValue(
                                                        'SocialAndEmotionalHealth.HowOftenDoYouSeeOrTalkToPeople.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('SocialAndEmotionalHealth.IsAIHowOftenDoYouSeeOrTalkToPeople', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel sx={isBHDiagnostic ? highlightStyle : undefined} required={isBHDiagnostic}>
                                                {AssessmentQuestions.PrepareSDOH_HowStressedAreYou}
                                                {values.SocialAndEmotionalHealth?.IsAIHowStressedAreYou && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="SocialAndEmotionalHealth.HowStressedAreYou.Answer"
                                                value={values.SocialAndEmotionalHealth?.HowStressedAreYou?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('SocialAndEmotionalHealth.HowStressedAreYou.Answer', e.currentTarget.value);
                                                    setFieldValue('SocialAndEmotionalHealth.HowStressedAreYou.NoAnswerToQuestion', false);
                                                    setFieldValue('SocialAndEmotionalHealth.IsAIHowStressedAreYou', false);
                                                }}
                                            >
                                                {stressLevelList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer13-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.SocialAndEmotionalHealth?.HowStressedAreYou?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('SocialAndEmotionalHealth.HowStressedAreYou.Answer', null);
                                                    setFieldValue('SocialAndEmotionalHealth.HowStressedAreYou.NoAnswerToQuestion', e.currentTarget.checked);
                                                    setFieldValue('SocialAndEmotionalHealth.IsAIHowStressedAreYou', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>

                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Typography variant="h6" component="label">
                                                Optional Additional Questions
                                            </Typography>
                                            <hr />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            {AssessmentQuestions.PrepareSDOH_DoYouFeelPhysicallyAndEmotionallySafeWhereYouCurrentlyLive}
                                            {values.OptionalAdditionalQuestions?.IsAIDoYouFeelPhysicallyAndEmotionallySafe && isAIIcon()}{' '}
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="OptionalAdditionalQuestions.DoYouFeelPhysicallyAndEmotionallySafe.Answer"
                                                value={values.OptionalAdditionalQuestions?.DoYouFeelPhysicallyAndEmotionallySafe?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue(
                                                        'OptionalAdditionalQuestions.DoYouFeelPhysicallyAndEmotionallySafe.Answer',
                                                        e.currentTarget.value
                                                    );
                                                    setFieldValue(
                                                        'OptionalAdditionalQuestions.DoYouFeelPhysicallyAndEmotionallySafe.NoAnswerToQuestion',
                                                        false
                                                    );
                                                    setFieldValue('OptionalAdditionalQuestions.IsAIDoYouFeelPhysicallyAndEmotionallySafe', false);
                                                }}
                                            >
                                                {emotionallySafeList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer14-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.OptionalAdditionalQuestions?.DoYouFeelPhysicallyAndEmotionallySafe?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('OptionalAdditionalQuestions.DoYouFeelPhysicallyAndEmotionallySafe.Answer', null);
                                                    setFieldValue(
                                                        'OptionalAdditionalQuestions.DoYouFeelPhysicallyAndEmotionallySafe.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('OptionalAdditionalQuestions.IsAIDoYouFeelPhysicallyAndEmotionallySafe', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <hr className="hr-lite" />
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <FormLabel>
                                                {sdohAssessment.Id === '' ? 'In the past year, have you been' : 'Are you currently'} afraid of your partner or
                                                ex-partner?
                                                {values.OptionalAdditionalQuestions?.IsAIHaveYouBeenAfraidOfYourPartner && isAIIcon()}
                                            </FormLabel>
                                        </Grid>
                                        <Grid size={{ sm: 6, md: 6, lg: 6 }}>
                                            <RadioGroup
                                                row
                                                name="OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.Answer"
                                                value={values.OptionalAdditionalQuestions?.HaveYouBeenAfraidOfYourPartner?.Answer}
                                                onChange={(e) => {
                                                    setFieldValue('OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.Answer', e.currentTarget.value);
                                                    setFieldValue('OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.NoAnswerToQuestion', false);
                                                    setFieldValue('OptionalAdditionalQuestions.IsAIHaveYouBeenAfraidOfYourPartner', false);
                                                }}
                                            >
                                                {afraidOfPartnerList.map((type, index) => {
                                                    return (
                                                        <FormControlLabel
                                                            key={`answer14-label-${index}`}
                                                            value={type.Answer}
                                                            control={<Radio />}
                                                            label={type.Answer}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Grid>
                                        <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                                            <Checkbox
                                                color="primary"
                                                checked={values.OptionalAdditionalQuestions?.HaveYouBeenAfraidOfYourPartner?.NoAnswerToQuestion}
                                                onChange={(e) => {
                                                    setFieldValue('OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.Answer', null);
                                                    setFieldValue(
                                                        'OptionalAdditionalQuestions.HaveYouBeenAfraidOfYourPartner.NoAnswerToQuestion',
                                                        e.currentTarget.checked
                                                    );
                                                    setFieldValue('OptionalAdditionalQuestions.IsAIHaveYouBeenAfraidOfYourPartner', false);
                                                }}
                                            />
                                            <FormLabel className="no-answer-text">I choose not to answer this question</FormLabel>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        <Grid justifyContent="flex-end" className="content-footer">
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                sx={{ mr: 3 }}
                                disabled={isSaving}
                                onClick={() => dispatch(gotoPreviousMenu())}
                                startIcon={<ArrowBackIosIcon />}
                            >
                                Previous
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSaving}
                                endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                            >
                                Next
                            </Button>
                        </Grid>
                    </Grid>
                    <SdohNeedsIdentifiedModal
                        isOpen={showNeedsIdentifiedModal}
                        handleClose={() => setShowNeedsIdentifiedModal(false)}
                        handleSubmit={(sdohAssessment: SdohAssessment) => {
                            dispatch(prepareSdohRequest(sdohAssessment));
                            dispatch(gotoNextMenu());
                        }}
                    />
                    {Boolean(sdohIsComplete(values)) || !isBHDiagnostic ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="SDOH Assessment"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => {
                                saveSdoh(values, true);
                                setShowNeedsIdentifiedModal(true);
                            }}
                            onSkip={() => saveSdoh(values)}
                            showBottomCenter={true}
                            isLoading={isSaving}
                            onClose={() => setShowSaveAlert(false)}
                            message="I confirm and acknowledge that SDOH assessment is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Current Needs"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => {
                                saveSdoh(values, true);
                                setShowNeedsIdentifiedModal(true);
                            }}
                            onSkip={() => saveSdoh(values)}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            renderBody={() =>
                                BHDiagnosticConfirmationBody({
                                    assessmentName: 'SDOH',
                                    diagnosticType: engagementDraft.DiagnosticStatus?.DiagnosticType ?? DiagnosticType.None,
                                })
                            }
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default Sdoh;
